import React, { useState, useEffect } from 'react';
import axios from 'axios';

interface BlogPost {
  id: number;
  title: string;
  excerpt: string;
  category: string;
  date: string;
  readTime: string;
  postUrl: string;
  image: string;
}

// Helper: strip HTML tags
const stripHtml = (html: string) => {
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || '';
};

// Helper: truncate to max words
const truncateWords = (text: string, maxWords: number) => {
  const words = text.split(/\s+/);
  if (words.length <= maxWords) return text;
  return words.slice(0, maxWords).join(' ') + '...';
};

// Helper: calculate read time
const calculateReadTime = (content: string) => {
  const wordCount = content.split(/\s+/).length;
  const minutes = Math.max(1, Math.ceil(wordCount / 200));
  return `${minutes} min read`;
};

// Helper: decode HTML entities like &amp; → &, &lt; → <, etc.
const decodeHtml = (html: string) => {
  const txt = document.createElement('textarea');
  txt.innerHTML = html;
  return txt.value;
};

const API_BASE_URL = process.env.REACT_APP_WP_API_URL;
const WP_URL = process.env.REACT_APP_WP_URL;

const BlogPosts = () => {
  const [blogPosts, setBlogPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const fetchPosts = async () => {
      
      try {
        const response = await axios.get(
          `${API_BASE_URL}/posts?per_page=${process.env.REACT_APP_WP_POSTS_PER_PAGE}&_embed`
        );

        console.log('Raw API response:', API_BASE_URL); // Debug log

        const posts: BlogPost[] = response.data.map((post: any) => {
          // Featured image
          let imageUrl = '';
          if (post._embedded && post._embedded['wp:featuredmedia']) {
            const media = post._embedded['wp:featuredmedia'][0];
            imageUrl = media.source_url || media.media_details?.sizes?.large?.source_url || '';
          }
          if (!imageUrl) {
            imageUrl = 'https://via.placeholder.com/800x400?text=No+Image';
          }

          // Category
          let categoryName = 'Uncategorized';
          if (post._embedded && post._embedded['wp:term']) {
            const categories = post._embedded['wp:term'][0];
            if (categories && categories.length > 0) {
              categoryName = decodeHtml(categories[0].name);
            }
          }

          // Title and excerpt (excerpt truncated to 30 words)
          const titleText = stripHtml(post.title.rendered);
          const excerptText = truncateWords(stripHtml(post.excerpt.rendered), 30);

          // Date formatting
          const formattedDate = new Date(post.date).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          });

          // Read time
          const fullContent = stripHtml(post.content?.rendered || '');
          const readTime = calculateReadTime(fullContent);

          return {
            id: post.id,
            title: titleText,
            excerpt: excerptText,
            category: categoryName,
            date: formattedDate,
            readTime: readTime,
            postUrl: post.link,
            image: imageUrl,
          };
        });

        setBlogPosts(posts);
      } catch (err) {
        setError(err instanceof Error ? err : new Error(String(err)));
      } finally {
        setLoading(false);
      }
    };

    fetchPosts();
  }, []);

  if (loading) return <div>Loading recent articles...</div>;
  if (error) return <div>Error loading recent articles: {error.message}</div>;

  return (
    <div className="bg-white pt-16 sm:pt-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center">
          <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">
            Latest Insights From Our Expert Team
          </h2>
          <p className="mt-3 max-w-2xl mx-auto text-xl text-gray-500">
            Discover our most recent articles and stay ahead of the competition.
          </p>
        </div>

        <div className="mt-12 grid gap-8 lg:grid-cols-3">
          {blogPosts.map((post) => (
            <div key={post.id} className="flex flex-col rounded-lg shadow-lg overflow-hidden">
              <div className="flex-shrink-0">
                <img className="h-48 w-full object-cover" src={post.image} alt={post.title} />
              </div>
              <div className="flex-1 bg-white p-6 flex flex-col justify-between">
                <div className="flex-1">
                  <p className="text-sm font-medium text-primary">{post.category}</p>
                  <a href={post.postUrl} className="block mt-2" target="_blank" rel="noopener noreferrer">
                    <h3 className="text-xl font-semibold text-gray-900">{post.title}</h3>
                    <p className="mt-3 text-base text-gray-500">{post.excerpt}</p>
                  </a>
                </div>
                <div className="mt-6 flex items-center">
                  <div className="flex space-x-1 text-sm text-gray-500">
                    <time dateTime={post.date}>{post.date}</time>
                    <span aria-hidden="true">&middot;</span>
                    <span>{post.readTime}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12 text-center">
          <a
            href={`${WP_URL}`}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
          >
            View All Articles
          </a>
        </div>
      </div>
    </div>
  );
};

export default BlogPosts;